import React, { useState, useEffect, useRef } from 'react'

export default function RawGoogleAutocomplete({ apiKey, onPlaceSelected, isErrored }) {
  const [input, setInput] = useState('')
  const [placeSelected, setPlaceSelected] = useState(false)
  const [predictions, setPredictions] = useState([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1) // track which item is highlighted
  const autocompleteService = useRef(null)
  const placesService = useRef(null)
  const sessionToken = useRef(null)

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
      script.async = true
      script.onload = () => {
        initServices()
      }
      document.head.appendChild(script)
    } else {
      initServices()
    }
  }, [apiKey])

  function initServices() {
    sessionToken.current = new window.google.maps.places.AutocompleteSessionToken()
    autocompleteService.current = new window.google.maps.places.AutocompleteService()
    placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'))
  }

  useEffect(() => {
    if (input.length < 3 || !autocompleteService.current) {
      setPredictions([])
      setHighlightedIndex(-1)
      return
    }

    autocompleteService.current.getPlacePredictions(
      {
        input,
        sessionToken: sessionToken.current,
        componentRestrictions: { country: 'au' }
      },
      (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && preds && !placeSelected) {
          setPredictions(preds)
          setHighlightedIndex(-1) // reset highlight on new results
        } else {
          setPredictions([])
          setHighlightedIndex(-1)
        }
      }
    )
  }, [input])

  useEffect(() => {
    if (!input) {
      setPlaceSelected(false)
    }
  }, [input])

  function handleSelect(placeId) {
    if (!placesService.current) return

    placesService.current.getDetails(
      {
        placeId,
        sessionToken: sessionToken.current,
        fields: ['formatted_address', 'geometry', 'address_components']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          console.log('in here place ', place)
          setPredictions([]) // clear suggestions

          setInput(place.formatted_address) // update input value immediately
          setPlaceSelected(true)
          setHighlightedIndex(-1) // reset highlight

          // Call your parent or handler with full place details
          onPlaceSelected(place)

          // Refresh session token to avoid stale session issues
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken()
        }
      }
    )
  }

  function handleKeyDown(e) {
    if (predictions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % predictions.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev <= 0 ? predictions.length - 1 : prev - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelect(predictions[highlightedIndex].place_id)
        }
        break
      case 'Escape':
        setPredictions([])
        setHighlightedIndex(-1)
        break
    }
  }

  return (
    <div style={{ position: 'relative', width: '60%' }}>
      <input
        data-testid={'address-input'}
        type='text'
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder='Enter an address'
        className={`${isErrored ? 'error-input verifier-input' : 'verifier-input'}`}
      />
      {predictions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #ccc',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            margin: 0,
            padding: 0,
            listStyle: 'none'
          }}
        >
          {predictions.map((pred, i) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
            <li
              data-testid={`pred-${i}`}
              key={pred.place_id}
              onClick={() => handleSelect(pred.place_id)}
              onMouseEnter={() => setHighlightedIndex(i)}
              style={{
                padding: '8px',
                cursor: 'pointer',
                backgroundColor: i === highlightedIndex ? '#ddd' : 'white'
              }}
            >
              {pred.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
